import { SimplifyGatsbyQuery, Header as HeaderCore, GlobalContextType } from "@parksandresorts/core";
import { graphql } from "gatsby";
import { GlobalContext } from "components/stores/global-context";
import LoginLink from "components/block-elements/login-link/login-link";
import { useContext } from "react";
import { LanguageSelectionList } from "@parksandresorts/core/dist/components/header/header-types.js";

type HeaderProps = {
  siteName?: string;
  siteConfig: SimplifyGatsbyQuery<Queries.SiteConfigurationFragment>;
  isStartPage: boolean;
  prefillHeader: boolean;
  isShowingTopBanner: boolean;
  pageHeader: SimplifyGatsbyQuery<Queries.HeaderFragment>;
};

const Header = ({
  siteName = "Sommarland",
  siteConfig,
  isStartPage,
  prefillHeader,
  isShowingTopBanner,
  pageHeader,
}: HeaderProps) => {
  const context = useContext(GlobalContext);

  const siteMenuBackgroundPatternUrls = pageHeader?.siteMenuBackgroundPattern && {
    default: pageHeader.siteMenuBackgroundPattern.file.url,
    highQuality: pageHeader.siteMenuBackgroundPattern.highQuality?.images.fallback?.src ?? "",
    lowQuality: pageHeader.siteMenuBackgroundPattern.lowQuality?.images.fallback?.src ?? "",
  };

  const transformedSiteMenuLinkGroups = pageHeader?.siteMenuLinkGroups?.map((group) => ({
    ...group,
    listObjects: group.listObjects?.filter(
      (item): item is Extract<typeof item, { __typename: "ContentfulLink" }> => item.__typename === "ContentfulLink",
    ),
  }));

  const transformedPageHeader = {
    ...pageHeader,
    siteMenuLinkGroups: transformedSiteMenuLinkGroups,
    languageSelectionList: pageHeader.languageSelectionList as LanguageSelectionList | undefined,
  };

  return (
    <HeaderCore
      {...transformedPageHeader}
      siteName={siteName}
      siteConfig={siteConfig}
      isStartPage={isStartPage}
      prefillHeader={prefillHeader}
      isShowingTopBanner={isShowingTopBanner}
      setContextValues={(values: Partial<GlobalContextType>) => context.setValue(values)}
      contextValues={context}
      siteMenuBackgroundPatternUrls={siteMenuBackgroundPatternUrls}
      loginLink={<LoginLink />}
    />
  );
};

export const fragmentHeader = graphql`
  fragment Header on ContentfulHeader {
    __typename
    id
    siteLogo {
      internalLink {
        slug
      }
      image {
        file {
          url
        }

        gatsbyImageData(width: 600, layout: FIXED, placeholder: NONE, quality: 100)
      }
    }
    isExtraWideSiteLogo
    menuLogo {
      file {
        url
      }
      gatsbyImageData(height: 150, layout: FIXED, placeholder: NONE, quality: 100)
    }
    siteMenuLinkGroups {
      id
      headline
      secondary
      listObjects {
        __typename
        ... on ContentfulLink {
          id
          title
          internalLink {
            slug
          }
          externalUrl
        }
      }
    }
    siteMenuBackgroundPattern {
      highQuality: gatsbyImageData(width: 600, quality: 100)
      lowQuality: gatsbyImageData(width: 300, quality: 70)
      file {
        url
      }
    }
    languageSelectionList {
      languages {
        url
        title
        locale
      }
    }
  }
`;

export default Header;
